/**
 * useBillingUsage — fetch + memoize this org's usage snapshot for the sidebar
 * meter (checks used vs. the monthly cap).
 *
 * Mirrors useOrgInfo's module-level promise cache, with one addition: a
 * subscriber set so invalidateBillingUsage() can push a fresh value into an
 * ALREADY-MOUNTED meter. The sidebar ring is always on screen, so it must
 * update right after an audit meters new checks — without waiting for a remount.
 * The backend reads a single row, so re-fetching is cheap.
 */
import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/apiFetch'

export interface BillingUsage {
  /** False when there's no billing row (dev/unprovisioned) — meter hides. */
  metered: boolean
  plan?: string | null
  status?: string | null
  checks_used?: number
  /** Published allowance; null when no cap applies (unlimited) — meter hides. */
  cap?: number | null
  period_reset_at?: string | null
  enforcement_enabled?: boolean
}

const NOT_METERED: BillingUsage = { metered: false }

let cached: Promise<BillingUsage> | null = null
const listeners = new Set<() => void>()

async function fetchUsage(): Promise<BillingUsage> {
  try {
    const res = await apiFetch('/api/billing/usage')
    if (!res.ok) return NOT_METERED
    return (await res.json()) as BillingUsage
  } catch {
    return NOT_METERED
  }
}

function getUsage(): Promise<BillingUsage> {
  if (!cached) cached = fetchUsage()
  return cached
}

/** Drop the cache and re-fetch into every mounted meter. Call after an audit
 *  completes (new checks metered) or a plan change. */
export function invalidateBillingUsage(): void {
  cached = null
  listeners.forEach((notify) => notify())
}

export function useBillingUsage(): { usage: BillingUsage | null; loading: boolean } {
  const [usage, setUsage] = useState<BillingUsage | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = () => {
      getUsage().then((u) => {
        if (cancelled) return
        setUsage(u)
        setLoading(false)
      })
    }
    load()
    listeners.add(load)
    return () => {
      cancelled = true
      listeners.delete(load)
    }
  }, [])

  return { usage, loading }
}
